import type { TAttentionStatusSeverity } from '../model/types'

import type { TAttentionStatusRole } from './roles'

/**
 * i18n key registry for the words the attention-status entity supplies itself: how urgent a
 * severity is, and what the primary action of a status is called.
 *
 * The statuses' own titles come from the payload, so they are data and stay strings; everything the
 * cockpit says *about* a status is a key, and a status the mapping does not know falls back rather
 * than going unnamed.
 */
export const ATTENTION_STATUSES_NAMESPACE = 'jbp-attention-statuses'

/**
 * The word each severity level carries in the UI; the number stays only as the colour badge.
 * The payload decides which levels exist, so a level with no word of its own reads as `Other`
 * rather than leaving the badge unnamed.
 */
export const ATTENTION_SEVERITY_NAME_KEYS: Record<number, string> = {
  3: `${ATTENTION_STATUSES_NAMESPACE}-severity-highest`,
  2: `${ATTENTION_STATUSES_NAMESPACE}-severity-high`,
  1: `${ATTENTION_STATUSES_NAMESPACE}-severity-standard`,
}

export const ATTENTION_SEVERITY_DEFAULT_NAME_KEY = `${ATTENTION_STATUSES_NAMESPACE}-severity-other`

export const getAttentionSeverityNameKey = (severity: TAttentionStatusSeverity): string =>
  ATTENTION_SEVERITY_NAME_KEYS[severity] ?? ATTENTION_SEVERITY_DEFAULT_NAME_KEY

/**
 * Primary action key per status bit, following the `Expanded view Actions` column of
 * `plans/P360-6305/Attention Statuses.xlsx`. The payload carries no action of its own yet, so the
 * mapping lives here until it does; a bit it does not know falls back to the neutral action.
 */
export const ATTENTION_STATUS_ACTION_KEYS: Record<number, string> = {
  1024: `${ATTENTION_STATUSES_NAMESPACE}-action-open-actions`,
  32: `${ATTENTION_STATUSES_NAMESPACE}-action-update-sob`,
  64: `${ATTENTION_STATUSES_NAMESPACE}-action-review`,
  16: `${ATTENTION_STATUSES_NAMESPACE}-action-acknowledge`,
  128: `${ATTENTION_STATUSES_NAMESPACE}-action-open-section`,
  256: `${ATTENTION_STATUSES_NAMESPACE}-action-create-initiative`,
  512: `${ATTENTION_STATUSES_NAMESPACE}-action-justify`,
  4: `${ATTENTION_STATUSES_NAMESPACE}-action-improve`,
  2: `${ATTENTION_STATUSES_NAMESPACE}-action-improve`,
  8: `${ATTENTION_STATUSES_NAMESPACE}-action-create-initiative`,
  1: `${ATTENTION_STATUSES_NAMESPACE}-action-open-section`,
}

/** Fallback for a bit the mapping has no action for, so a newer catalog still renders a row. */
export const ATTENTION_STATUS_DEFAULT_ACTION_KEY = `${ATTENTION_STATUSES_NAMESPACE}-action-open`

export const getAttentionStatusActionKey = (bitValue: number): string =>
  ATTENTION_STATUS_ACTION_KEYS[bitValue] ?? ATTENTION_STATUS_DEFAULT_ACTION_KEY

/** What a row offers a role that only reads the status: the row opens it, nothing more. */
export const ATTENTION_STATUS_SEE_DETAILS_ACTION_KEY = `${ATTENTION_STATUSES_NAMESPACE}-action-see-details`

/**
 * Lead sentence per status bit: what the detail below it shows, said before the reader reads it.
 * Every catalog row has one, so an opened status never starts on bare numbers.
 */
export const ATTENTION_STATUS_INTRO_KEYS: Record<number, string> = {
  1024: `${ATTENTION_STATUSES_NAMESPACE}-intro-pending-actions`,
  32: `${ATTENTION_STATUSES_NAMESPACE}-intro-sob-actuals`,
  64: `${ATTENTION_STATUSES_NAMESPACE}-intro-h2-review`,
  16: `${ATTENTION_STATUSES_NAMESPACE}-intro-acknowledgment`,
  128: `${ATTENTION_STATUSES_NAMESPACE}-intro-mdf-eligibility`,
  256: `${ATTENTION_STATUSES_NAMESPACE}-intro-program-risk`,
  512: `${ATTENTION_STATUSES_NAMESPACE}-intro-quarter-plan`,
  4: `${ATTENTION_STATUSES_NAMESPACE}-intro-initiatives-quality`,
  2: `${ATTENTION_STATUSES_NAMESPACE}-intro-finsob-quality`,
  8: `${ATTENTION_STATUSES_NAMESPACE}-intro-mdf-planning`,
  1: `${ATTENTION_STATUSES_NAMESPACE}-intro-completeness`,
}

/** Fallback for a bit the mapping has no lead sentence for, so a newer catalog still reads. */
export const ATTENTION_STATUS_DEFAULT_INTRO_KEY = `${ATTENTION_STATUSES_NAMESPACE}-intro-default`

/** Lead sentences a PBM reads in place of the shared one: the plan is their own, so the sentence
 *  speaks to them rather than of `the PBM`. */
export const ATTENTION_STATUS_PBM_INTRO_KEYS: Record<number, string> = {
  1: `${ATTENTION_STATUSES_NAMESPACE}-intro-completeness-pbm`,
}

/** A distributor's plan is signed by its DBM rather than a PBM, so its lead sentences name the DBM:
 *  one for the DBM reading their own plan, one for everyone else. */
export const ATTENTION_STATUS_DISTRIBUTOR_PBM_INTRO_KEYS: Record<number, string> = {
  1: `${ATTENTION_STATUSES_NAMESPACE}-intro-completeness-pbm-distributor`,
}

export const ATTENTION_STATUS_DISTRIBUTOR_INTRO_KEYS: Record<number, string> = {
  1: `${ATTENTION_STATUSES_NAMESPACE}-intro-completeness-distributor`,
}

export const getAttentionStatusIntroKey = (
  bitValue: number,
  role?: TAttentionStatusRole,
  isDistributor = false
): string =>
  (role === 'pbm' && isDistributor
    ? ATTENTION_STATUS_DISTRIBUTOR_PBM_INTRO_KEYS[bitValue]
    : undefined) ??
  (role === 'pbm' ? ATTENTION_STATUS_PBM_INTRO_KEYS[bitValue] : undefined) ??
  (isDistributor ? ATTENTION_STATUS_DISTRIBUTOR_INTRO_KEYS[bitValue] : undefined) ??
  ATTENTION_STATUS_INTRO_KEYS[bitValue] ??
  ATTENTION_STATUS_DEFAULT_INTRO_KEY

/** `Remind DBM` — the PBM reminder as a distributor's plan names it. */
export const ATTENTION_STATUS_REMIND_DBM_ACTION_KEY = `${ATTENTION_STATUSES_NAMESPACE}-action-remind-dbm`

/**
 * What a reminder's recipient is called on its own, without the verb: the grouped `Remind` control
 * names the act once and its list names the people, so `Remind PBM` would say `Remind` twice.
 */
export const ATTENTION_STATUS_RECIPIENT_LABEL_KEYS: Record<string, string> = {
  pbm: `${ATTENTION_STATUSES_NAMESPACE}-recipient-pbm`,
  manager: `${ATTENTION_STATUSES_NAMESPACE}-recipient-manager`,
  partner: `${ATTENTION_STATUSES_NAMESPACE}-recipient-partner`,
}

/** What the grouped reminder control itself is called. */
export const ATTENTION_STATUS_REMIND_GROUP_LABEL_KEY = `${ATTENTION_STATUSES_NAMESPACE}-action-remind`
